import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Building, Edit, Users, Key, Trash2 } from 'lucide-react';

interface Organization {
  id: string;
  name: string;
  status?: string;
  plan?: string;
  settings?: any;
  user_count?: number;
  license_count?: number;
  createdAt?: number;
  created_at?: number;
}

function Organizations() {
  const { t } = useTranslation();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '' });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    checkUserAndFetchOrganizations();
  }, []);

  const checkUserAndFetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // First try to get current organization
      const orgResponse = await fetch(`${apiUrl}/api/organizations/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (orgResponse.ok) {
        const orgData = await orgResponse.json();

        // Check if this is a SUPER_ADMIN (no organization)
        if (orgData.data?.isSuperAdmin) {
          setIsSuperAdmin(true);
          // Fetch all organizations for SUPER_ADMIN
          await fetchAllOrganizations();
        } else {
          // Regular user - set their organization
          setOrganization(orgData.data);
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      setIsLoading(false);
    }
  };

  const fetchAllOrganizations = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/organizations?page=${page}&limit=20`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.data?.organizations || []);
        if (data.data?.pagination) {
          setPagination({
            page: data.data.pagination.page,
            totalPages: data.data.pagination.totalPages,
            total: data.data.pagination.total,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDialog = () => {
    if (organization) {
      setEditForm({
        name: organization.name,
      });
      setIsEditDialogOpen(true);
    }
  };

  const handleUpdateOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/organizations/current`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        checkUserAndFetchOrganizations();
      }
    } catch (error) {
      console.error('Failed to update organization:', error);
    }
  };

  const openDeleteDialog = (orgId: string) => {
    setSelectedOrgId(orgId);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrgId) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/organizations/${selectedOrgId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setDeleteDialogOpen(false);
        setSelectedOrgId(null);
        setDeleteError(null);
        fetchAllOrganizations(pagination.page);
      } else {
        setDeleteError(data.error || 'Failed to delete organization');
      }
    } catch (error) {
      console.error('Failed to delete organization:', error);
      setDeleteError('Failed to delete organization');
    }
  };

  const getPlanBadgeVariant = (plan: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      ENTERPRISE: 'default',
      PRO: 'default',
      BASIC: 'secondary',
      FREE: 'outline',
    };
    return variants[plan] || 'outline';
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // SUPER_ADMIN view - show all organizations
  if (isSuperAdmin) {
    return (
      <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <PageHeader
          eyebrow="Platform workspace"
          icon={Building}
          title={t('organizations.title')}
          subtitle={t('organizations.allOrganizations', { count: pagination.total })}
        />

        <Card>
          <CardHeader>
            <CardTitle>{t('organizations.allOrganizationsTitle', 'All Organizations')}</CardTitle>
            <CardDescription>
              {pagination.total} {t('organizations.registered', 'organizations registered')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('organizations.name')}</TableHead>
                  <TableHead>{t('organizations.status', 'Status')}</TableHead>
                  <TableHead>{t('organizations.plan', 'Plan')}</TableHead>
                  <TableHead>{t('organizations.users', 'Users')}</TableHead>
                  <TableHead>{t('organizations.licenses', 'Licenses')}</TableHead>
                  <TableHead>{t('licenses.createdAt')}</TableHead>
                  <TableHead className="text-right">{t('users.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      {t('dashboard.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{org.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={org.status || 'ACTIVE'} />
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPlanBadgeVariant(org.plan || 'FREE')}>
                          {org.plan || 'FREE'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {org.user_count || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Key className="h-4 w-4 text-muted-foreground" />
                          {org.license_count || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatDate(org.created_at || org.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(org.id)}
                          disabled={(org.user_count || 0) > 0 || (org.license_count || 0) > 0}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title={(org.user_count || 0) > 0 || (org.license_count || 0) > 0
                            ? 'Cannot delete organization with users or licenses'
                            : 'Delete organization'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAllOrganizations(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    {t('common.previous', 'Previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAllOrganizations(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    {t('common.next', 'Next')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Organization Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('organizations.dialogs.delete.title', 'Delete Organization')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('organizations.dialogs.delete.description', 'Are you sure you want to delete this organization? This action cannot be undone.')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {deleteError && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {deleteError}
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteError(null)}>
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteOrganization}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('common.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Regular user view - show single organization
  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Configuration"
        icon={Building}
        title={t('organizations.title')}
        subtitle={t('organizations.subtitle')}
        actions={(
          <Button onClick={openEditDialog}>
            <Edit className="h-4 w-4 mr-2" />
            {t('organizations.editSettings')}
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('organizations.details')}</CardTitle>
          <CardDescription>
            {t('organizations.detailsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {organization ? (
            <>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('organizations.name')}</label>
                <p className="text-lg">{organization.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('organizations.created')}</label>
                <p className="text-lg">
                  {formatDate(organization.created_at || organization.createdAt)}
                </p>
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground">{t('organizations.noOrganization')}</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Organization Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('organizations.dialogs.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('organizations.dialogs.edit.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('organizations.dialogs.edit.orgName')}</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Your Organization"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdateOrganization} disabled={!editForm.name}>
              {t('organizations.dialogs.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Organizations;
